import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ReferenceStock } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceStock';
import { CatalogSource } from '@1po/1po-bff-fe-spec/generated/common/vehicle/CatalogSource';
import { ROUTER_CATALOG_DH_L3 } from 'app/AppRouter';
import { BagIcon, CompareCompactIcon } from 'assets/icons';
import { ExadisInterestedButton } from 'components/ExadisInterestedButton';
import { FirstHelpPopin } from 'components/Help/FirstHelpPopin';
import { isEmptyStock } from 'components/StockInfo/EmptyStock';
import { DATAHUB } from 'domains/catalog/Catalog.types';
import { BlackButton, ButtonShape, Flex, GreyButton, Icon, YellowCartArrowDownButton } from 'UI';
import { FOUND, SearchData } from 'utils';

type ButtonSize = 'small' | 'middle' | 'large';

const LARGE_BUTTON_WIDTH = 250;
const DEFAULT_BUTTON_WIDTH = 180;

const getButtonContainerMinWidth = (stretchCartButton: boolean, buttonSize: ButtonSize): string | number => {
  if (stretchCartButton) {
    return '100%';
  }
  return buttonSize === 'large' ? LARGE_BUTTON_WIDTH : DEFAULT_BUTTON_WIDTH;
};

const getButtonContainerMaxWidth = (
  stretchCartButton: boolean,
  buttonSize: ButtonSize,
): string | number | undefined => {
  if (stretchCartButton) {
    return undefined;
  }
  return buttonSize === 'large' ? LARGE_BUTTON_WIDTH : DEFAULT_BUTTON_WIDTH;
};

const getButtonContainerAlign = (
  externalButton: ReactNode,
  stretchCartButton: boolean,
): 'stretch' | 'flex-end' | undefined => {
  if (externalButton) {
    return undefined;
  }
  return stretchCartButton ? 'stretch' : 'flex-end';
};

interface PriceButtonsModuleProps {
  referenceNumber: string;
  catalogSource: CatalogSource;
  handleAddToCartClick: (sellerName?: string, sellerNumber?: string) => void;
  stock: SearchData<ReferenceStock> | undefined;
  isExadis?: boolean;
  isAvailableSoon?: boolean;
  isNoLongerAvailable?: boolean;
  enabled?: boolean;
  withIcon?: boolean;
  showFirstHelp?: boolean;
  useCompactView?: boolean;
  externalButton?: ReactNode;
  buttonSize?: ButtonSize;
  buttonShape?: ButtonShape;
  handleClickCompare?: (reference: string) => void;
  handleMultipleOffersButtonClick?: React.Dispatch<React.SetStateAction<boolean>>;
  isMKTP?: boolean;
  sellerNumber?: string;
  sellerName?: string;
  referenceWithMultipleOffers?: boolean;
  stretchCartButton?: boolean;
}

const PriceButtonsModule = ({
  referenceNumber,
  catalogSource,
  handleAddToCartClick,
  stock,
  isExadis,
  isAvailableSoon,
  isNoLongerAvailable,
  enabled = true,
  withIcon = false,
  showFirstHelp,
  useCompactView = false,
  externalButton,
  buttonSize = 'middle',
  buttonShape = 'default',
  handleMultipleOffersButtonClick,
  handleClickCompare,
  isMKTP = false,
  sellerNumber,
  sellerName,
  referenceWithMultipleOffers,
  stretchCartButton = false,
}: PriceButtonsModuleProps) => {
  const { t } = useTranslation();
  const buttonContainerMinWidth = getButtonContainerMinWidth(stretchCartButton, buttonSize);
  const buttonContainerMaxWidth = getButtonContainerMaxWidth(stretchCartButton, buttonSize);
  const buttonContainerSize = stretchCartButton ? undefined : 0;
  const buttonRowMinWidth = stretchCartButton ? '100%' : undefined;

  const renderCartButtons = () => {
    const isOutOfStock = !stock || isEmptyStock(stock);

    if (useCompactView) {
      return (
        <>
          {handleClickCompare && (
            <Icon
              IconComponent={CompareCompactIcon}
              height={42}
              width={48}
              onClick={() => handleClickCompare(referenceNumber)}
            />
          )}
          <YellowCartArrowDownButton
            onClick={() => handleAddToCartClick(sellerName, sellerNumber)}
            data-cy={`add-to-cart-ref-${referenceNumber}`}
            round
            size={24}
            boxSize={'smd'}
            disabled={isOutOfStock}
          />
        </>
      );
    }
    if (!isNoLongerAvailable && !isAvailableSoon) {
      if (enabled) {
        return (
          <BlackButton
            size={buttonSize}
            onClick={() => handleAddToCartClick(sellerName, sellerNumber)}
            stretch
            data-cy={`add-to-cart-ref-${referenceNumber}`}
            shape={buttonShape}
            disabled={isOutOfStock}
          >
            <Flex align={'center'} justify={'center'} gap={5}>
              {withIcon && <Icon IconComponent={BagIcon} size={20} />}
              {t('cart.action.add_to_cart', 'Add to cart')}
            </Flex>
          </BlackButton>
        );
      }
      return (
        <GreyButton
          size={buttonSize}
          onClick={() => handleAddToCartClick(sellerName, sellerNumber)}
          stretch
          data-cy={`add-to-cart-ref-${referenceNumber}`}
          shape={buttonShape}
          disabled={isOutOfStock}
        >
          <Flex align={'center'} justify={'center'} gap={5}>
            {withIcon && <Icon IconComponent={BagIcon} size={20} />}
            {t('cart.action.add_to_cart', 'Add to cart')}
          </Flex>
        </GreyButton>
      );
    }
    return null;
  };

  const renderCartButtonsWithFirstHelpPopin = () => {
    const showCartButtons =
      (catalogSource === DATAHUB || (stock?.searchStatus === FOUND && !isEmptyStock(stock))) && !isNoLongerAvailable;

    return showCartButtons ? (
      <FirstHelpPopin
        streamId={ROUTER_CATALOG_DH_L3}
        popinId={`${ROUTER_CATALOG_DH_L3}_add_to_cart`}
        placement={'left'}
        skip={!showFirstHelp}
      >
        {renderCartButtons()}
      </FirstHelpPopin>
    ) : null;
  };

  if (referenceWithMultipleOffers && isMKTP && !sellerNumber) {
    return (
      <Flex
        minWidth={buttonContainerMinWidth}
        maxWidth={buttonContainerMaxWidth}
        size={buttonContainerSize}
        direction="column"
        align={externalButton ? undefined : 'flex-end'}
      >
        <BlackButton
          size={buttonSize}
          onClick={() => handleMultipleOffersButtonClick?.(true)}
          stretch
          shape={buttonShape}
        >
          <Flex align={'center'} justify={'center'} gap={5}>
            {t('common.action.all_offers', 'All offers')}
          </Flex>
        </BlackButton>
      </Flex>
    );
  }

  return (
    <Flex
      minWidth={buttonContainerMinWidth}
      maxWidth={buttonContainerMaxWidth}
      size={buttonContainerSize}
      direction="column"
      align={getButtonContainerAlign(externalButton, stretchCartButton)}
    >
      {externalButton ? (
        <Flex
          direction={'row'}
          justify={'space-between'}
          align={'center'}
          gap={20}
          wrap={'nowrap'}
          minWidth={buttonRowMinWidth}
        >
          {externalButton}
          {renderCartButtonsWithFirstHelpPopin()}
          {isExadis && <ExadisInterestedButton catalogSource={catalogSource} referenceNumber={referenceNumber} />}
        </Flex>
      ) : (
        <Flex
          direction={'row'}
          justify={'space-between'}
          align={'center'}
          gap={20}
          wrap={'nowrap'}
          minWidth={buttonRowMinWidth}
        >
          {renderCartButtonsWithFirstHelpPopin()}
          {isExadis && <ExadisInterestedButton catalogSource={catalogSource} referenceNumber={referenceNumber} />}
        </Flex>
      )}
    </Flex>
  );
};

export default PriceButtonsModule;
